import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import {
  CustomElementColumnDetail,
  SelectedWo,
  UdvData,
  WorkOrder,
  WorkOrderGroup,
  WorkOrderMetadata,
} from "../models/workorders/WorkOrder";
import { WoDocument } from "../models/workorders/Documents";
import { DescriptionMetadata } from "../models/workorders/Description";
import {
  MeterReadings,
  MeterReadingsMetadata,
} from "../models/workorders/MeterReading";
import { PlantCurrency } from "../models/plant/Plant";
import { CompletePermitDetails } from "../models/workorders/Permit";
import {
  Craft,
  LaborLogFields,
  LaborsMetadata,
  Rate,
} from "../models/workorders/LaborActivity";
import {
  GetColumnsWithGroupField,
  GetColumnsWithGroupResponse,
  ValidValue,
} from "../models/windows/GetColumnsWithGroup";
import { devtools, persist } from "zustand/middleware";
import localforage from "localforage";
import {
  AnalysisCode,
  CascadeValue,
} from "../models/analysiscodes/AnalysisCodes";
import { unionBy } from "lodash";
import { ItemListEntry } from "../pages/MaterialIssue";
import { LinkedInspection } from "../api/WorkOrders";

export const DEFAULT_PERMISSIONS = {
  Comment: false,
  TimeEntry: false,
  Meter: false,
  Inspections: false,
  CCA: false,
  IssueMaterial: false,
  ReturnMaterial: false,
  status: false,
};

type customElementColumn = { [key: string]: CustomElementColumnDetail };
type WoOrderActionsPermission = {
  Comment: boolean;
  TimeEntry: boolean;
  Meter: boolean;
  Inspections: boolean;
  CCA: boolean;
  IssueMaterial: boolean;
  ReturnMaterial: boolean;
  status: boolean;
};

export type InspectionData = {
  inspectionResults?: any;
  inspectionDef?: any;
  isCompleted: boolean;
  inspectionId?: string;
};

type CraftData = {
  Crafts: Craft[];
  PrimaryCraftCode: string;
};

type DefaultFilterData = {
  UserParameter: string;
  UserSection: string;
  UserValue: string;
  Userid: string;
};

export type TestEquipment = {
  Equipment: string;
  ManufacturerSerialId: string;
  LastCalibrated: string;
  CalibrationExpiresDate: string;
};

type WorkOrderState = {
  workOrders: WorkOrder[];
  documents: { [woNumber: string]: WoDocument[] };
  descriptionMetaData: { [woNumber: string]: DescriptionMetadata };
  groups: { [woNumber: string]: WorkOrderGroup[] };
  metaData: { [woNumber: string]: WorkOrderMetadata };
  udvList: { [woNumber: string]: UdvData[] };
  customElementColumnDetail: {
    [woNumber: string]: customElementColumn;
  };
  meterReadings: { [woNumber: string]: MeterReadings[] };
  actionPermissions: { [woNumber: string]: WoOrderActionsPermission };
  resourcesFields: { [woNumber: string]: any[] };
  plantCurrency: { [woNumber: string]: PlantCurrency };
  completedPermits: { [woNumber: string]: CompletePermitDetails[] };
  comments: { [woNumber: string]: string };
  laborLogFields: { [woNumber: string]: LaborLogFields[] };
  laborLogs: { [woNumber: string]: any[] };
  hasTimeEntryAccess: { [woNumber: string]: boolean };
  validStatusValues: { [woNumber: string]: ValidValue[] };
  analysisCodes: { [woNumber: string]: AnalysisCode[] };
  ccaFieldDefinitions: { [woNumber: string]: any };
  completeColumnDetails: { [woNumber: string]: any };
  meterReadingsHistory: { [meter: string]: MeterReadings[] };
  inspectionData: { [meter: string]: InspectionData[] };
  linkedInspections: { [woNumber: string]: LinkedInspection[] };
  selectedWo: SelectedWo[];
  downloadedWo: SelectedWo[];
  issueItems: { [woNumber: string]: ItemListEntry[] };
  returnItems: { [woNumber: string]: ItemListEntry[] };
  caaIsRequiredForWoCompletion: { [woNumber: string]: boolean };
  isInspectionAllowedForWo: { [woNumber: string]: boolean };

  //data, not specific to work order
  meterReadingsHistoryMetaData?: MeterReadingsMetadata;
  meterMetaData?: MeterReadingsMetadata;
  newMeterMetaData?: MeterReadingsMetadata;
  noOfMeterHistoryDisplay?: number;
  createWoMetaData?: WorkOrderMetadata;
  customFields?: any[];
  cascadeValues?: CascadeValue[];
  timeEntryMetaData?: LaborsMetadata;
  crafts?: CraftData;
  rateList?: Rate[];
  isNewRelationship: boolean;
  woCompletionFields?: GetColumnsWithGroupField[];
  woDefaultCompletionStatus?: string;
  issueColumns?: GetColumnsWithGroupResponse[];
  returnColumns?: GetColumnsWithGroupResponse[];
  negativeQtyOnHandMessage?: string;
  defaultFilterForIssueScreen?: DefaultFilterData;
  testEquipRule?: string;
  testEquipmentList?: TestEquipment[];
  woMaxStatus?: string;
};

type WorkOrderAction = {
  addUpdateWorkOrder: (woOrder: WorkOrder) => void;
  getWorkOrderById: (woId: string) => WorkOrder | undefined;
  setWoDocuments: (woNumber: string, docs: WoDocument[]) => void;
  getDocumentsForWo: (woNumber: string) => WoDocument[];
  getDocumentCountForWo: (woNumber: string) => number;
  setWoDescriptionMetaData: (
    woNumber: string,
    metaData: DescriptionMetadata
  ) => void;
  getMetaDescriptionForWo: (woNumber: string) => DescriptionMetadata;
  setWoGroups: (woNumber: string, groups: WorkOrderGroup[]) => void;
  getGroupsForWo: (woNumber: string) => WorkOrderGroup[];
  setWoMetaData: (woNumber: string, metaData: WorkOrderMetadata) => void;
  getMetaDataForWo: (woNumber: string) => WorkOrderMetadata;
  setCreateWoMetaData: (metaData: WorkOrderMetadata) => void;
  setWoUdvList: (woNumber: string, udvList: UdvData[]) => void;
  getUdvListForWo: (woNumber: string) => UdvData[] | undefined;
  setWoCustomElementColumnDetail: (
    woNumber: string,
    customElementColumnDetail: customElementColumn
  ) => void;
  getCustomElementColumnDetailForWo: (woNumber: string) => customElementColumn;

  setWoActionsPermissions: (
    woNumber: string,
    permissions: WoOrderActionsPermission
  ) => void;
  getActionPermissionsForWo: (
    woNumber: string
  ) => WoOrderActionsPermission | undefined;

  setWoResourcesFields: (woNumber: string, fields: any[]) => void;
  getResourcesFieldsForWo: (woNumber: string) => any[] | undefined;

  setCaaIsRequiredForWoCompletion: (
    woNumber: string,
    isRequired: boolean
  ) => void;
  getCaaIsRequiredForWoCompletion: (woNumber: string) => boolean | undefined;

  setIsInspectionAllowedForWo: (woNumber: string, isAllowed: boolean) => void;
  getIsInspectionAllowedForWo: (woNumber: string) => boolean | undefined;

  setLinkedInspectionsForWo: (
    woNumber: string,
    inspections: LinkedInspection[]
  ) => void;
  getLinkedInspectionsForWo: (
    woNumber: string
  ) => LinkedInspection[] | undefined;

  setWoPlantCurrency: (woNumber: string, plantCurrency: PlantCurrency) => void;
  getPlantCurrencyForWo: (woNumber: string) => PlantCurrency;

  setWoCompletedPermits: (
    woNumber: string,
    permits: CompletePermitDetails[]
  ) => void;
  getCompletedPermitsForWo: (
    woNumber: string
  ) => CompletePermitDetails[] | undefined;
  setWoComments: (woNumber: string, comments: string) => void;
  getCommentsForWo: (woNumber: string) => string | undefined;

  //meter
  setWoMeterMetadata: (metadata: MeterReadingsMetadata) => void;
  getMeterMetadataForWo: () => MeterReadingsMetadata | undefined;
  getMeterReadingByMeter: (
    meter: string,
    woNumber: string
  ) => MeterReadings | undefined;
  setWoMeterHistoryMetadata: (metadata: MeterReadingsMetadata) => void;
  getMeterHistoryMetadataForWo: () => MeterReadingsMetadata | undefined;
  setNewMeterMetadata: (metadata: MeterReadingsMetadata) => void;
  setWoMeterReadings: (
    woNumber: string,
    meterReadings: MeterReadings[]
  ) => void;
  getMeterReadingsForWo: (woNumber: string) => MeterReadings[] | undefined;
  setMeterReadingsHistory: (
    meter: string,
    meterReadings: MeterReadings[]
  ) => void;
  getMeterReadingsHistoryForMeter: (
    meter: string
  ) => MeterReadings[] | undefined;
  setWoNoOfMeterHistoryDisplay: (noOfDisplay: number) => void;

  setWoLaborLogFields: (woNumber: string, fields: LaborLogFields[]) => void;
  getLaborLogFieldsForWo: (woNumber: string) => LaborLogFields[] | undefined;
  setWoLaborLog: (woNumber: string, fields: any[]) => void;
  getLaborLogForWo: (woNumber: string) => any[] | undefined;
  setWoValidStatusValues: (woNumber: string, values: ValidValue[]) => void;
  getValidStatusValuesForWo: (woNumber: string) => ValidValue[] | undefined;
  setWoHasTimeEntryAccess: (woNumber: string, hasAccess: boolean) => void;
  getHasTimeEntryAccessForWo: (woNumber: string) => boolean | undefined;
  setWoAnalysisCodes: (woNumber: string, analysisCode: AnalysisCode[]) => void;
  getAnalysisCodesForWo: (woNumber: string) => AnalysisCode[] | undefined;
  setWoCCAFieldDefinitions: (woNumber: string, definitions: any) => void;
  getCCAFieldDefinitionsForWo: (woNumber: string) => any;
  setWoCompleteColumnDetails: (woNumber: string, details: any) => void;
  getInspectionDataForWo: (
    woNumber: string,
    inspectionId: string
  ) => InspectionData | undefined;
  setWoInspectionData: (woNumber: string, data: InspectionData) => void;
  getCompleteColumnDetailsForWo: (woNumber: string) => any;
  setWoIssueItems: (woNumber: string, items: ItemListEntry[]) => void;
  getIssueItemForWo: (woNumber: string) => ItemListEntry[] | undefined;
  setWoReturnItems: (woNumber: string, items: ItemListEntry[]) => void;
  getReturnItemForWo: (woNumber: string) => ItemListEntry[] | undefined;

  setCustomFields: (fields: any[]) => void;
  toggleWo: (item: SelectedWo) => void;
  resetSelectedWo: () => void;
  setDownloadedWo: (items: SelectedWo[]) => void;
  setCascadeValues: (values: CascadeValue[]) => void;
  setTimeEntryMetaData: (metaData: LaborsMetadata) => void;
  setCarts: (carts: CraftData) => void;
  setRateList: (rate: Rate[]) => void;
  setIsNewRelationship: (isNew: boolean) => void;
  setWoCompletionFields: (values: GetColumnsWithGroupField[]) => void;
  setWoDefaultCompletionStatus: (value: string) => void;
  setIssueColumns: (columns: GetColumnsWithGroupResponse[]) => void;
  setReturnColumns: (columns: GetColumnsWithGroupResponse[]) => void;
  setNegativeQtyOnHandMessage: (msg: string) => void;
  setDefaultFilterForIssueScreen: (filter: DefaultFilterData) => void;
  setTestEquipmentList: (list: TestEquipment[]) => void;
  setTestEquipRule: (rule: string) => void;
  setWoMaxStatus: (status: string) => void;
  reset: () => void;
  resetWoByWoNumber: (woNumber: string) => void;
  resetAllButDownloadedWos: () => void;
  markInspectionCompleted: (woNumber: string, inspectionId: string) => void;
};

const defaultState: WorkOrderState = {
  documents: {},
  workOrders: [],
  descriptionMetaData: {},
  groups: {},
  metaData: {},
  customElementColumnDetail: {},
  udvList: {},
  meterReadings: {},
  actionPermissions: {},
  resourcesFields: {},
  plantCurrency: {},
  completedPermits: {},
  comments: {},
  meterMetaData: undefined,
  hasTimeEntryAccess: {},
  laborLogFields: {},
  laborLogs: {},
  validStatusValues: {},
  analysisCodes: {},
  ccaFieldDefinitions: {},
  completeColumnDetails: {},
  meterReadingsHistoryMetaData: undefined,
  meterReadingsHistory: {},
  noOfMeterHistoryDisplay: undefined,
  downloadedWo: [],
  selectedWo: [],
  newMeterMetaData: undefined,
  inspectionData: {},
  createWoMetaData: undefined,
  customFields: undefined,
  cascadeValues: undefined,
  crafts: undefined,
  isNewRelationship: false,
  rateList: undefined,
  timeEntryMetaData: undefined,
  woCompletionFields: undefined,
  woDefaultCompletionStatus: undefined,
  issueItems: {},
  returnItems: {},
  issueColumns: undefined,
  returnColumns: undefined,
  negativeQtyOnHandMessage: undefined,
  defaultFilterForIssueScreen: undefined,
  testEquipmentList: undefined,
  testEquipRule: undefined,
  caaIsRequiredForWoCompletion: {},
  woMaxStatus: undefined,
  isInspectionAllowedForWo: {},
  linkedInspections: {},
};

export const useWorkOrderStore = create<WorkOrderState & WorkOrderAction>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...defaultState,
        getGroupsForWo(woNumber) {
          return get().groups[woNumber] ?? [];
        },
        getCustomElementColumnDetailForWo(woNumber) {
          return get().customElementColumnDetail[woNumber];
        },
        getResourcesFieldsForWo(woNumber) {
          return get().resourcesFields[woNumber];
        },
        getPlantCurrencyForWo(woNumber) {
          return get().plantCurrency[woNumber];
        },
        getCompletedPermitsForWo(woNumber) {
          return get().completedPermits[woNumber];
        },
        getCommentsForWo(woNumber) {
          return get().comments[woNumber];
        },
        getMeterMetadataForWo() {
          return get().meterMetaData;
        },
        getLaborLogFieldsForWo(woNumber) {
          return get().laborLogFields[woNumber];
        },
        getLaborLogForWo(woNumber) {
          return get().laborLogs[woNumber];
        },
        getValidStatusValuesForWo(woNumber) {
          return get().validStatusValues[woNumber];
        },
        getHasTimeEntryAccessForWo(woNumber) {
          return get().hasTimeEntryAccess[woNumber];
        },
        getAnalysisCodesForWo(woNumber) {
          return get().analysisCodes[woNumber];
        },
        getCCAFieldDefinitionsForWo(woNumber) {
          return get().ccaFieldDefinitions[woNumber];
        },
        getCompleteColumnDetailsForWo(woNumber) {
          return get().completeColumnDetails[woNumber];
        },
        getMeterHistoryMetadataForWo() {
          return get().meterReadingsHistoryMetaData;
        },
        getMeterReadingsHistoryForMeter(woNumber) {
          return get().meterReadingsHistory[woNumber];
        },

        getInspectionDataForWo(woNumber, inspectionId) {
          const inspectionsForWo = get().inspectionData[woNumber];
          if (!inspectionsForWo) {
            return undefined;
          }
          return inspectionsForWo.find((i) => i.inspectionId === inspectionId);
        },

        getIssueItemForWo(woNumber) {
          return get().issueItems[woNumber];
        },

        getReturnItemForWo(woNumber) {
          return get().returnItems[woNumber];
        },

        setWoDefaultCompletionStatus(value) {
          set((state) => {
            state.woDefaultCompletionStatus = value;
          });
        },

        getLinkedInspectionsForWo(woNumber) {
          return get().linkedInspections[woNumber];
        },

        setLinkedInspectionsForWo(woNumber, inspections) {
          set((state) => {
            state.linkedInspections = {
              ...state.linkedInspections,
              [woNumber]: inspections,
            };
          });
        },

        setIssueColumns(columns) {
          set((state) => {
            state.issueColumns = columns;
          });
        },

        setWoMaxStatus(status) {
          set((state) => {
            state.woMaxStatus = status;
          });
        },

        setReturnColumns(columns) {
          set((state) => {
            state.returnColumns = columns;
          });
        },

        setIsNewRelationship(isNew) {
          set((state) => {
            state.isNewRelationship = isNew;
          });
        },

        setRateList(rate) {
          set((state) => {
            state.rateList = rate;
          });
        },

        setCascadeValues(values) {
          set((state) => {
            state.cascadeValues = values;
          });
        },

        setTimeEntryMetaData(metaData) {
          set((state) => {
            state.timeEntryMetaData = metaData;
          });
        },

        setWoCompletionFields(values) {
          set((state) => {
            state.woCompletionFields = values;
          });
        },

        setCreateWoMetaData(metaData) {
          set((state) => {
            state.createWoMetaData = metaData;
          });
        },
        setCustomFields(fields) {
          set((state) => {
            state.customFields = fields;
          });
        },
        setTestEquipmentList(list) {
          set((state) => {
            state.testEquipmentList = list;
          });
        },
        setTestEquipRule(rule) {
          set((state) => {
            state.testEquipRule = rule;
          });
        },
        setWoInspectionData(woNumber, data) {
          set((state) => {
            state.inspectionData = {
              ...state.inspectionData,
              [woNumber]: [
                ...((state.inspectionData[woNumber] ?? []).filter(
                  (i) => i.inspectionId !== data.inspectionId
                ) ?? []),
                data,
              ],
            };
          });
        },
        setWoIssueItems(woNumber, items) {
          set((state) => {
            state.issueItems = {
              ...state.issueItems,
              [woNumber]: items,
            };
          });
        },

        setCaaIsRequiredForWoCompletion(woNumber, isRequired) {
          set((state) => {
            state.caaIsRequiredForWoCompletion = {
              ...state.caaIsRequiredForWoCompletion,
              [woNumber]: isRequired,
            };
          });
        },

        getCaaIsRequiredForWoCompletion(woNumber) {
          return get().caaIsRequiredForWoCompletion[woNumber];
        },

        setIsInspectionAllowedForWo(woNumber, isAllowed) {
          set((state) => {
            state.isInspectionAllowedForWo = {
              ...state.isInspectionAllowedForWo,
              [woNumber]: isAllowed,
            };
          });
        },

        getIsInspectionAllowedForWo(woNumber) {
          return get().isInspectionAllowedForWo[woNumber];
        },

        setWoReturnItems(woNumber, items) {
          set((state) => {
            state.returnItems = {
              ...state.returnItems,
              [woNumber]: items,
            };
          });
        },

        setWoNoOfMeterHistoryDisplay(noOfDisplay) {
          set((state) => {
            state.noOfMeterHistoryDisplay = noOfDisplay;
          });
        },
        setMeterReadingsHistory(woNumber, meterReadings) {
          set((state) => {
            state.meterReadingsHistory = {
              ...state.meterReadingsHistory,
              [woNumber]: meterReadings,
            };
          });
        },
        setWoMeterHistoryMetadata(metadata) {
          set((state) => {
            state.meterReadingsHistoryMetaData = metadata;
          });
        },
        setWoCompleteColumnDetails(woNumber, details) {
          set((state) => {
            state.completeColumnDetails = {
              ...state.completeColumnDetails,
              [woNumber]: details,
            };
          });
        },
        setWoCCAFieldDefinitions(woNumber, definitions) {
          set((state) => {
            state.ccaFieldDefinitions = {
              ...state.ccaFieldDefinitions,
              [woNumber]: definitions,
            };
          });
        },
        setWoAnalysisCodes(woNumber, analysisCode) {
          set((state) => {
            state.analysisCodes = {
              ...state.analysisCodes,
              [woNumber]: analysisCode,
            };
          });
        },
        setWoHasTimeEntryAccess(woNumber, hasAccess) {
          set((state) => {
            state.hasTimeEntryAccess = {
              ...state.hasTimeEntryAccess,
              [woNumber]: hasAccess,
            };
          });
        },
        setWoValidStatusValues(woNumber, values) {
          set((state) => {
            state.validStatusValues = {
              ...state.validStatusValues,
              [woNumber]: values,
            };
          });
        },
        setWoLaborLog(woNumber, fields) {
          set((state) => {
            state.laborLogs = {
              ...state.laborLogs,
              [woNumber]: fields,
            };
          });
        },
        setWoLaborLogFields(woNumber, fields) {
          set((state) => {
            state.laborLogFields = {
              ...state.laborLogFields,
              [woNumber]: fields,
            };
          });
        },
        setWoMeterMetadata(metadata) {
          set((state) => {
            state.meterMetaData = metadata;
          });
        },

        setWoComments(woNumber, comments) {
          set((state) => {
            state.comments = {
              ...state.comments,
              [woNumber]: comments,
            };
          });
        },
        setWoCompletedPermits(woNumber, permits) {
          set((state) => {
            state.completedPermits = {
              ...state.completedPermits,
              [woNumber]: permits,
            };
          });
        },
        setWoPlantCurrency(woNumber, plantCurrency) {
          set((state) => {
            state.plantCurrency = {
              ...state.plantCurrency,
              [woNumber]: plantCurrency,
            };
          });
        },
        setWoResourcesFields(woNumber, fields) {
          set((state) => {
            state.resourcesFields = {
              ...state.resourcesFields,
              [woNumber]: fields,
            };
          });
        },
        getActionPermissionsForWo(woNumber) {
          const permissions = get().actionPermissions[woNumber];
          return permissions;
        },
        setWoActionsPermissions(woNumber, permissions) {
          set((state) => {
            state.actionPermissions = {
              ...state.actionPermissions,
              [woNumber]: permissions,
            };
          });
        },
        getMeterReadingsForWo(woNumber) {
          return get().meterReadings[woNumber];
        },

        setCarts(carts) {
          set((state) => {
            state.crafts = carts;
          });
        },

        setWoMeterReadings(woNumber, meterReadings) {
          set((state) => {
            state.meterReadings = {
              ...state.meterReadings,
              [woNumber]: meterReadings,
            };
          });
        },
        setNewMeterMetadata(metadata) {
          set((state) => {
            state.newMeterMetaData = metadata;
          });
        },
        getMeterReadingByMeter(meter, woNumber) {
          const meterReadings = get().meterReadings[woNumber];
          return (meterReadings ?? []).find((m) => m.Meter === meter);
        },
        setWoCustomElementColumnDetail(woNumber, customElementColumnDetail) {
          set((state) => {
            state.customElementColumnDetail = {
              ...state.customElementColumnDetail,
              [woNumber]: customElementColumnDetail,
            };
          });
        },
        getUdvListForWo(woNumber) {
          return get().udvList[woNumber];
        },
        setWoUdvList(woNumber, udvList) {
          set((state) => {
            state.udvList = { ...state.udvList, [woNumber]: udvList };
          });
        },
        setWoGroups(woNumber, groups) {
          set((state) => {
            state.groups = {
              ...state.groups,
              [woNumber]: groups,
            };
          });
        },
        getMetaDataForWo(woNumber) {
          return get().metaData[woNumber];
        },
        setWoMetaData(woNumber, metaData) {
          set((state) => {
            state.metaData = {
              ...state.metaData,
              [woNumber]: metaData,
            };
          });
        },
        getMetaDescriptionForWo(woNumber) {
          const { descriptionMetaData } = get();
          return descriptionMetaData[woNumber];
        },
        setWoDescriptionMetaData(woNumber, metaData) {
          set((state) => {
            state.descriptionMetaData = {
              ...state.descriptionMetaData,
              [woNumber]: metaData,
            };
          });
        },
        setWoDocuments(woNumber, docs) {
          set((state) => {
            state.documents = { ...state.documents, [woNumber]: docs };
          });
        },
        getDocumentsForWo(woNumber) {
          const { documents } = get();
          return documents[woNumber];
        },
        getDocumentCountForWo(woNumber) {
          return (get().documents[woNumber] ?? []).length;
        },
        addUpdateWorkOrder(woOrder) {
          set((state) => {
            const woOrders = [...state.workOrders];

            const woIndex = woOrders.findIndex(
              (w) => w.CfWorkOrder === woOrder.CfWorkOrder
            );
            if (woIndex > -1) {
              // Replace existing item
              woOrders[woIndex] = woOrder;
            } else {
              // Add new item
              woOrders.push(woOrder);
            }
            state.workOrders = woOrders;
          });
        },
        getWorkOrderById(woId) {
          const wo = get().workOrders.find((w) => w.CfWorkOrder === woId);
          return wo;
        },
        setDownloadedWo(items) {
          set((state) => {
            state.downloadedWo = unionBy(
              items,
              [...state.downloadedWo],
              (i) => i.id
            );
          });
        },
        setNegativeQtyOnHandMessage(msg) {
          set((state) => {
            state.negativeQtyOnHandMessage = msg;
          });
        },
        setDefaultFilterForIssueScreen(filter) {
          set((state) => {
            state.defaultFilterForIssueScreen = filter;
          });
        },
        markInspectionCompleted(woNumber, inspectionId) {
          const { inspectionData, setWoInspectionData } = get();
          const insp = inspectionData[woNumber]?.find(
            (i) => i.inspectionId?.toString() === inspectionId.toString()
          );
          if (insp) {
            setWoInspectionData(woNumber, { ...insp, isCompleted: true });
          }
        },
        resetSelectedWo() {
          set((state) => {
            state.selectedWo = [];
          });
        },
        toggleWo(item) {
          set((state) => {
            if (state.selectedWo.some((s) => s.id === item.id))
              state.selectedWo = state.selectedWo.filter(
                (s) => s.id !== item.id
              );
            else state.selectedWo = [...state.selectedWo, item];
          });
        },
        reset() {
          set(() => defaultState);
        },
        resetWoByWoNumber(woNumber) {
          set((state) => {
            const removeKey = (obj: Record<string, any>) => {
              const { [woNumber]: _, ...rest } = obj;
              return rest;
            };

            state.workOrders = state.workOrders.filter(
              (s) => s.CfWorkOrder !== woNumber
            );
            state.descriptionMetaData = removeKey(state.descriptionMetaData);
            state.documents = removeKey(state.documents);
            state.groups = removeKey(state.groups);
            state.metaData = removeKey(state.metaData);
            state.customElementColumnDetail = removeKey(
              state.customElementColumnDetail
            );
            state.udvList = removeKey(state.udvList);
            state.meterReadings = removeKey(state.meterReadings);
            state.actionPermissions = removeKey(state.actionPermissions);
            state.resourcesFields = removeKey(state.resourcesFields);
            state.plantCurrency = removeKey(state.plantCurrency);
            state.completedPermits = removeKey(state.completedPermits);
            state.comments = removeKey(state.comments);
            state.hasTimeEntryAccess = removeKey(state.hasTimeEntryAccess);
            state.laborLogFields = removeKey(state.laborLogFields);
            state.laborLogs = removeKey(state.laborLogs);
            state.validStatusValues = removeKey(state.validStatusValues);
            state.analysisCodes = removeKey(state.analysisCodes);
            state.ccaFieldDefinitions = removeKey(state.ccaFieldDefinitions);
            state.completeColumnDetails = removeKey(
              state.completeColumnDetails
            );
            state.meterReadingsHistory = removeKey(state.meterReadingsHistory);
            state.inspectionData = removeKey(state.inspectionData);
            state.issueItems = removeKey(state.issueItems);
            state.returnItems = removeKey(state.returnItems);
            state.caaIsRequiredForWoCompletion = removeKey(
              state.caaIsRequiredForWoCompletion
            );
            state.isInspectionAllowedForWo = removeKey(
              state.isInspectionAllowedForWo
            );
            state.linkedInspections = removeKey(state.linkedInspections);
          });
        },
        resetAllButDownloadedWos() {
          set((state) => {
            const idsToKeep = state.downloadedWo.map((d) => d.id);
            const retainKeys = (obj: Record<string, any>) => {
              return Object.fromEntries(
                Object.entries(obj).filter(([key]) => idsToKeep.includes(key))
              );
            };
            state.workOrders = state.workOrders.filter(
              (s) => s.CfWorkOrder && idsToKeep.includes(s.CfWorkOrder)
            );
            state.documents = retainKeys(state.documents);
            state.groups = retainKeys(state.groups);
            state.metaData = retainKeys(state.metaData);
            state.customElementColumnDetail = retainKeys(
              state.customElementColumnDetail
            );
            state.udvList = retainKeys(state.udvList);
            state.meterReadings = retainKeys(state.meterReadings);
            state.actionPermissions = retainKeys(state.actionPermissions);
            state.resourcesFields = retainKeys(state.resourcesFields);
            state.plantCurrency = retainKeys(state.plantCurrency);
            state.completedPermits = retainKeys(state.completedPermits);
            state.comments = retainKeys(state.comments);
            state.hasTimeEntryAccess = retainKeys(state.hasTimeEntryAccess);
            state.laborLogFields = retainKeys(state.laborLogFields);
            state.laborLogs = retainKeys(state.laborLogs);
            state.validStatusValues = retainKeys(state.validStatusValues);
            state.analysisCodes = retainKeys(state.analysisCodes);
            state.ccaFieldDefinitions = retainKeys(state.ccaFieldDefinitions);
            state.completeColumnDetails = retainKeys(
              state.completeColumnDetails
            );
            state.meterReadingsHistory = retainKeys(state.meterReadingsHistory);
            state.inspectionData = retainKeys(state.inspectionData);
            state.issueItems = retainKeys(state.issueItems);
            state.returnItems = retainKeys(state.returnItems);
            state.caaIsRequiredForWoCompletion = retainKeys(
              state.caaIsRequiredForWoCompletion
            );
            state.isInspectionAllowedForWo = retainKeys(
              state.isInspectionAllowedForWo
            );
            state.linkedInspections = retainKeys(state.linkedInspections);
          });
        },
      })),
      {
        name: "offline-work-orders",
        storage: {
          getItem: (key: string): Promise<any> => localforage.getItem(key),
          setItem: (key: string, value: any): Promise<void> =>
            localforage.setItem(key, value),
          removeItem: (key: string): Promise<void> =>
            localforage.removeItem(key),
        },
        partialize: (state) => ({
          // ✅ only persist this
          documents: state.documents,
          workOrders: state.workOrders,
          descriptionMetaData: state.descriptionMetaData,
          groups: state.groups,
          metaData: state.metaData,
          customElementColumnDetail: state.customElementColumnDetail,
          udvList: state.udvList,
          meterReadings: state.meterReadings,
          actionPermissions: state.actionPermissions,
          resourcesFields: state.resourcesFields,
          plantCurrency: state.plantCurrency,
          completedPermits: state.completedPermits,
          comments: state.comments,
          meterMetaData: state.meterMetaData,
          hasTimeEntryAccess: state.hasTimeEntryAccess,
          laborLogFields: state.laborLogFields,
          laborLogs: state.laborLogs,
          validStatusValues: state.validStatusValues,
          analysisCodes: state.analysisCodes,
          ccaFieldDefinitions: state.ccaFieldDefinitions,
          completeColumnDetails: state.completeColumnDetails,
          meterReadingsHistoryMetaData: state.meterReadingsHistoryMetaData,
          meterReadingsHistory: state.meterReadingsHistory,
          downloadedWo: state.downloadedWo,
          newMeterMetaData: state.newMeterMetaData,
          noOfMeterHistoryDisplay: state.noOfMeterHistoryDisplay,
          inspectionData: state.inspectionData,
          createWoMetaData: state.createWoMetaData,
          customFields: state.customFields,
          cascadeValues: state.cascadeValues,
          timeEntryMetaData: state.timeEntryMetaData,
          crafts: state.crafts,
          rateList: state.rateList,
          isNewRelationship: state.isNewRelationship,
          woCompletionFields: state.woCompletionFields,
          woDefaultCompletionStatus: state.woDefaultCompletionStatus,
          issueItems: state.issueItems,
          returnItems: state.returnItems,
          issueColumns: state.issueColumns,
          returnColumns: state.returnColumns,
          negativeQtyOnHandMessage: state.negativeQtyOnHandMessage,
          defaultFilterForIssueScreen: state.defaultFilterForIssueScreen,
          testEquipmentList: state.testEquipmentList,
          testEquipRule: state.testEquipRule,
          caaIsRequiredForWoCompletion: state.caaIsRequiredForWoCompletion,
          woMaxStatus: state.woMaxStatus,
          isInspectionAllowedForWo: state.isInspectionAllowedForWo,
          linkedInspections: state.linkedInspections,
        }),
      }
    ),
    {
      name: "WorkOrderStore",
      enabled: true,
    }
  )
);
